import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ChevronRight, ChevronDown, ArrowRight } from 'lucide-react';
import { printSectors, solutions, clientLogos, testimonials } from '../data/mockData';
import { useScrollReveal } from '../hooks/useScrollReveal';
import SEO from '../components/SEO';
import FAQSection from '../components/FAQSection';
import { sectorFaqs } from '../data/faqData';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const PrintSectorsPage = () => {
  const [heroRef, isHeroVisible] = useScrollReveal({ threshold: 0.05 });
  const [introRef, isIntroVisible] = useScrollReveal();
  const [sectorsRef, isSectorsVisible] = useScrollReveal();
  const [transRef, isTransVisible] = useScrollReveal();
  const [logosRef, isLogosVisible] = useScrollReveal();
  const [testRef, isTestVisible] = useScrollReveal();
  const [solRef, isSolVisible] = useScrollReveal();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent size={28} /> : null;
  };

  const doubledLogos = [...clientLogos, ...clientLogos];
  const doubledTestimonials = [...testimonials, ...testimonials];

  return (
    <div className="pt-0 lg:pt-28">
      <SEO
        title="Print Sectors — MIS Solutions for Every Print Business"
        description="Sector-specific MIS solutions for cartons, security print, mailers, books, magazines, labels and more. Discover the Imprint solution built for your business."
        url="https://imprint-mis.co.uk/print-sectors"
      />
      {/* Hero Banner */}
      <section className="relative overflow-hidden bg-[#0a0b10] py-28 lg:py-36">
        {/* Background effects */}
        <div className="absolute inset-0">
          <div className="absolute top-1/3 left-1/4 w-80 h-80 rounded-full opacity-10"
            style={{ background: 'radial-gradient(circle, #00BCE4 0%, transparent 70%)', animation: 'pulse-glow 6s ease-in-out infinite' }} />
          <div className="absolute bottom-1/4 right-1/3 w-60 h-60 rounded-full opacity-8"
            style={{ background: 'radial-gradient(circle, #00BCE4 0%, transparent 70%)', animation: 'pulse-glow 8s ease-in-out infinite 2s' }} />
          <div className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: 'linear-gradient(rgba(0,188,228,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(0,188,228,0.3) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
          {Array.from({ length: 20 }).map((_, i) => (
            <div key={i} className="absolute rounded-full"
              style={{
                width: Math.random() * 3 + 1 + 'px', height: Math.random() * 3 + 1 + 'px',
                left: Math.random() * 100 + '%', top: Math.random() * 100 + '%',
                background: i % 3 === 0 ? '#00BCE4' : '#ffffff',
                opacity: Math.random() * 0.4 + 0.1,
                animation: `float-particle ${Math.random() * 8 + 6}s ease-in-out infinite ${Math.random() * 5}s`,
              }} />
          ))}
        </div>

        <div ref={heroRef} className={`relative z-10 section-container reveal ${isHeroVisible ? 'visible' : ''}`}>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/50 mb-6">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} />
            <span className="text-[#00BCE4]">Print Sectors</span>
          </nav>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white leading-tight mb-6"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Print <span className="text-[#00BCE4]">Sectors</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-2xl leading-relaxed"
            style={{ fontFamily: 'Inter, sans-serif' }}>
            Discover MIS solutions built around your business needs — flexible, sector-specific, and designed to evolve with your company.
          </p>

          <ChevronDown size={32} className="text-[#00BCE4] mt-10 animate-bounce" />
        </div>
      </section>

      {/* "It's all about you" Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div ref={introRef} className={`section-container reveal ${isIntroVisible ? 'visible' : ''}`}>
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                It's all about <span className="text-[#00BCE4]">you...</span>
              </h2>
              <p className="text-xl text-gray-700 font-medium mb-6"
                style={{ fontFamily: 'Inter, sans-serif' }}>
                Ask yourself, <span className="text-[#00BCE4] font-bold">do we cater for you?</span>
              </p>
              <p className="text-base text-gray-500 leading-relaxed mb-8"
                style={{ fontFamily: 'Inter, sans-serif' }}>
                From single-site trade printers to multi-plant packaging groups, we&apos;ve spent decades tuning Imprint MIS to
                the quirks of every sector. If your business doesn&apos;t look like a textbook print shop, that&apos;s exactly who we build for.
              </p>

              <div className="relative rounded-2xl overflow-hidden mb-8 shadow-lg border border-gray-100">
                <img
                  src="/print-shop-quality-control.webp"
                  alt="Quality control on the print floor"
                  loading="lazy"
                  className="w-full h-64 md:h-72 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
                <div className="absolute bottom-4 left-5 right-5">
                  <span className="inline-block text-[10px] font-bold uppercase tracking-[2px] text-[#00BCE4] bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full mb-2">
                    Built for the print floor
                  </span>
                  <p className="text-white text-sm font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    Real workflows. Real production data. Real ROI.
                  </p>
                </div>
              </div>

              <Link
                to="/#contact"
                data-testid="print-sectors-book-demo-cta"
                className="btn-primary text-xs"
              >
                Book a Demo
                <ArrowRight size={16} />
              </Link>
            </div>
            <div className="space-y-5">
              <div className="p-5 rounded-xl bg-gray-50 border-l-4 border-[#00BCE4]">
                <p className="text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  These days it can be much harder to define a company with a single badge. Our customers often use a variety of our solutions to fit their business model.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-gray-50 border-l-4 border-[#00BCE4]">
                <p className="text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  There is no "one size fits all" approach. We produce dedicated systems to satisfy specific areas of the industry — not just an adapted version of the standard.
                </p>
              </div>
              <div className="p-5 rounded-xl bg-gray-50 border-l-4 border-[#00BCE4]">
                <p className="text-base text-gray-600 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  As well as Litho we have specific solutions for, but are not restricted to, the following areas:
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Print Sectors Grid with Accordions */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div ref={sectorsRef} className={`section-container reveal ${isSectorsVisible ? 'visible' : ''}`}>
          <Accordion type="single" collapsible className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {printSectors.map((sector, index) => (
              <AccordionItem
                key={sector.title}
                value={`sector-${index}`}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-[#00BCE4]/40 transition-all duration-300 data-[state=open]:border-[#00BCE4] data-[state=open]:shadow-xl data-[state=open]:shadow-[#00BCE4]/10"
              >
                <AccordionTrigger className="px-6 py-6 hover:no-underline group">
                  <div className="flex items-center gap-4 text-left">
                    <div className="w-14 h-14 rounded-xl bg-[#00BCE4]/10 flex items-center justify-center text-[#00BCE4] flex-shrink-0 group-hover:bg-[#00BCE4] group-hover:text-white transition-all duration-300">
                      {getIcon(sector.icon)}
                    </div>
                    <h3 className="text-base font-bold text-gray-900 group-hover:text-[#00BCE4] transition-colors duration-300"
                      style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {sector.title}
                    </h3>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-6">
                  <p className="text-sm text-gray-600 leading-relaxed pl-[4.5rem]"
                    style={{ fontFamily: 'Inter, sans-serif' }}>
                    {sector.description}
                  </p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Transitional Text */}
      <section className="relative py-20 lg:py-28 overflow-hidden">
        <div className="absolute inset-0">
          <img src="/computer-system.webp"
            alt="Imprint MIS Software Interface" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/80 to-black/70"></div>
        </div>

        {/* Pointing mascot */}
        <img src="/immi-pointing-nobg.webp" alt="Immi pointing"
          className="absolute right-8 lg:right-20 bottom-0 w-48 lg:w-64 h-auto hidden lg:block"
          style={{ animation: 'mascot-float 4s ease-in-out infinite', mixBlendMode: 'screen', filter: 'drop-shadow(0 10px 30px rgba(0,188,228,0.4))' }} />

        <div ref={transRef} className={`relative z-10 section-container reveal ${isTransVisible ? 'visible' : ''}`}>
          <div className="max-w-2xl">
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              The best way that Imprint can help you realise your vision is by getting to know you and your company.
            </p>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-6"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              We realise that undertaking a management information system (MIS) project is a big step and a big decision for any business.
            </p>
            <p className="text-lg md:text-xl text-gray-200 leading-relaxed mb-8"
              style={{ fontFamily: 'Inter, sans-serif' }}>
              Whether it's your first time choosing an MIS or looking to update an outdated system, there is a lot to consider.
            </p>
            <Link to="/#contact" className="btn-primary text-xs">
              Let's Talk
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-20 lg:py-24 bg-white overflow-hidden">
        <div ref={logosRef} className={`reveal ${isLogosVisible ? 'visible' : ''}`}>
          <div className="section-container mb-10">
            <span className="block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-2"
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              Here are just some of our clients
            </span>
          </div>
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10"></div>
            <div className="overflow-hidden">
              <div className="logo-scroll-track">
                {doubledLogos.map((logo, index) => (
                  <div key={`${logo.name}-${index}`} className="flex-shrink-0 mx-6 group">
                    <div className="w-48 h-28 rounded-xl border border-gray-200 bg-gray-50 flex items-center justify-center transition-all duration-300 group-hover:border-[#00BCE4]/40 group-hover:shadow-lg group-hover:bg-white">
                      <div className="text-center">
                        <span className="block text-2xl font-bold text-gray-400 group-hover:text-[#00BCE4] transition-colors duration-300"
                          style={{ fontFamily: 'Outfit, sans-serif' }}>{logo.initials}</span>
                        <span className="block text-[10px] text-gray-400 font-medium mt-1 uppercase tracking-wider"
                          style={{ fontFamily: 'Inter, sans-serif' }}>{logo.name}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 lg:py-24 bg-gray-50 overflow-hidden">
        <div ref={testRef} className={`reveal ${isTestVisible ? 'visible' : ''}`}>
          <div className="section-container mb-12">
            <span className="block text-sm font-semibold text-[#00BCE4] uppercase tracking-[3px] mb-3"
              style={{ fontFamily: 'Outfit, sans-serif' }}>Endorsements</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}>
              What our clients say<br /><span className="text-[#00BCE4]">about Imprint...</span>
            </h2>
          </div>
          <div className="relative">
            <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-gray-50 to-transparent z-10"></div>
            <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-gray-50 to-transparent z-10"></div>
            <div className="overflow-hidden">
              <div className="testimonial-scroll-track">
                {doubledTestimonials.map((testimonial, index) => (
                  <div key={`${testimonial.company}-${index}`} className="flex-shrink-0 mx-4 w-[420px]">
                    <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 h-full hover:shadow-xl hover:border-[#00BCE4]/20 transition-all duration-500">
                      <LucideIcons.Quote size={32} className="text-[#00BCE4] mb-4 opacity-40" />
                      <blockquote className="text-sm text-gray-600 leading-relaxed mb-6"
                        style={{ fontFamily: 'Inter, sans-serif' }}>
                        "{testimonial.quote}"
                      </blockquote>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#00BCE4]/10 flex items-center justify-center">
                          <span className="text-sm font-bold text-[#00BCE4]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {testimonial.company.charAt(0)}
                          </span>
                        </div>
                        <span className="text-sm font-semibold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          {testimonial.company}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <FAQSection
        id="sector-faq"
        eyebrow="Sector FAQs"
        title="Your sector, answered by"
        highlight="the technical team"
        faqs={sectorFaqs}
      />

      {/* Solutions CTA */}
      <section className="bg-[#00BCE4] py-20 lg:py-28 relative overflow-hidden">
        <img src="/immi-jumping-nobg.webp" alt="Immi jumping"
          className="absolute right-8 lg:right-16 bottom-0 w-56 lg:w-72 xl:w-80 h-auto hidden md:block"
          style={{ animation: 'mascot-float 3s ease-in-out infinite 0.5s', mixBlendMode: 'screen', filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.3))' }} />

        <div ref={solRef} className={`section-container text-center reveal ${isSolVisible ? 'visible' : ''}`}>
          <p className="text-sm md:text-base font-semibold text-white/80 uppercase tracking-[2px] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Whether it's an out-of-the-box system, or a bespoke solution, we know we can help!
          </p>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-12"
            style={{ fontFamily: 'Outfit, sans-serif' }}>
            Find your solution and ask<br className="hidden md:block" /> about integrations...
          </h2>

          <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
            {solutions.map((sol) => (
              <Link key={sol.title} to="/#solutions"
                className="inline-flex items-center gap-2 px-5 py-3 bg-white/15 hover:bg-white/25 text-white font-semibold text-sm rounded-lg border border-white/20 transition-all duration-300 backdrop-blur-sm hover:scale-105"
                style={{ fontFamily: 'Outfit, sans-serif' }}>
                {sol.title}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrintSectorsPage;
